import React, { useCallback, useEffect, useMemo, useState } from "react";
import ReactApexChart from "react-apexcharts";
import { NotificationManager } from "react-notifications";
import {
  Phone,
  PhoneCall,
  PhoneIncoming,
  PhoneOutgoing,
  Clock,
  TrendingUp,
  Users,
  Mic,
  Activity,
  RefreshCw,
  Timer,
} from "lucide-react";
import SiteWrapper from "../../../../SiteWrapper.react";
import { Crud_Catalogos } from "../../../../herramientas/Crud_Catalogos";
import {
  PROVEEDOR,
  fmtNumero,
  fmtDuracion,
  fmtPorcentaje,
  fmtGB,
  aFechaDesde,
  aFechaHasta,
  hoyISO,
  haceDiasISO,
  normalizar,
  extraerLista,
  pick,
} from "./dashboardUtils";
import "./dashboard.css";

const DashboardLlamadasPage = () => {
  const [fechaInicio, setFechaInicio] = useState(haceDiasISO(7));
  const [fechaFin, setFechaFin] = useState(hoyISO());
  const [cargando, setCargando] = useState(false);

  const [stats, setStats] = useState(null);
  const [porHora, setPorHora] = useState([]);
  const [porExtension, setPorExtension] = useState([]);
  const [recStats, setRecStats] = useState(null);
  const [liveCalls, setLiveCalls] = useState([]);

  /* ---------- Peticiones ---------- */
  const pedir = useCallback((tipo, extra = "") => {
    const f1 = encodeURIComponent(aFechaDesde(fechaInicio));
    const f2 = encodeURIComponent(aFechaHasta(fechaFin));
    const url = `softphone_v3/dashboard?provedor=${PROVEEDOR}&tipo=${tipo}&f1=${f1}&f2=${f2}${extra}`;
    return Crud_Catalogos(url, "softphone", "get", "", []);
  }, [fechaInicio, fechaFin]);

  const cargarTodo = useCallback(() => {
    setCargando(true);
    Promise.all([
      pedir("cdr_stats"),
      pedir("por_hora"),
      pedir("por_extension"),
      pedir("grabaciones"),
    ])
      .then(([rStats, rHora, rExt, rRec]) => {
        setStats(normalizar(rStats) || null);
        setPorHora(extraerLista(normalizar(rHora)));
        setPorExtension(extraerLista(normalizar(rExt)));
        setRecStats(normalizar(rRec) || null);
      })
      .catch(() => NotificationManager.error("Error al cargar el dashboard"))
      .finally(() => setCargando(false));
  }, [pedir]);

  const cargarLive = useCallback(() => {
    Crud_Catalogos(
      `softphone_v3/dashboard/live_calls?provedor=${PROVEEDOR}`,
      "softphone",
      "get",
      "",
      []
    )
      .then((res) => {
        const data = extraerLista(normalizar(res));
        setLiveCalls(data);
      })
      .catch(() => {
        /* silencioso: el polling no debe inundar de notificaciones */
      });
  }, []);

  useEffect(() => {
    cargarTodo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Polling de llamadas en vivo cada 10s */
  useEffect(() => {
    cargarLive();
    const t = setInterval(cargarLive, 10000);
    return () => clearInterval(t);
  }, [cargarLive]);

  /* ---------- KPIs derivados ---------- */
  const kpis = useMemo(() => {
    const s = stats || {};
    // Tolerante a nombres reales y documentados.
    const sinResp = pick(s, "missed_calls", "unanswered_calls");
    const avgDur = pick(s, "avg_duration", "avg_duration_seconds");
    const totalDur = pick(s, "total_duration", "total_duration_seconds");
    return [
      {
        label: "Total de llamadas",
        value: fmtNumero(s.total_calls),
        icon: <Phone size={22} />,
        accent: "#4f46e5",
        sub: `${fmtNumero(s.answered_calls)} contestadas`,
      },
      {
        label: "Tasa de respuesta",
        value: fmtPorcentaje(s.answer_rate),
        icon: <TrendingUp size={22} />,
        accent: "#10b981",
        sub: `${fmtNumero(sinResp)} sin respuesta`,
      },
      {
        label: "Duración promedio",
        value: fmtDuracion(avgDur),
        icon: <Clock size={22} />,
        accent: "#06b6d4",
        sub: `Total ${fmtDuracion(totalDur)}`,
      },
      {
        label: "Tiempo facturable",
        value: fmtDuracion(s.total_billsec),
        icon: <Timer size={22} />,
        accent: "#f59e0b",
        sub: `Prom. ${fmtDuracion(s.avg_billsec)}`,
      },
      {
        label: "Entrantes / Salientes",
        value: `${fmtNumero(s.inbound_calls)} / ${fmtNumero(s.outbound_calls)}`,
        icon: <PhoneIncoming size={22} />,
        accent: "#0ea5e9",
        sub: `${fmtNumero(s.internal_calls)} internas`,
      },
      {
        label: "Llamadas en vivo",
        value: fmtNumero(liveCalls.length),
        icon: <Activity size={22} />,
        accent: "#8b5cf6",
        sub: "En tiempo real",
        live: true,
      },
    ];
  }, [stats, liveCalls]);

  /* ---------- Gráfica: distribución de resultado (dona) ---------- */
  const donaDisposition = useMemo(() => {
    const s = stats || {};
    const series = [
      Number(s.answered_calls) || 0,
      Number(pick(s, "missed_calls", "unanswered_calls")) || 0,
      Number(s.busy_calls) || 0,
      Number(s.failed_calls) || 0,
    ];
    return {
      series,
      options: {
        chart: { type: "donut", fontFamily: "inherit" },
        labels: ["Contestadas", "Sin respuesta", "Ocupadas", "Fallidas"],
        colors: ["#10b981", "#64748b", "#f59e0b", "#ef4444"],
        legend: { position: "bottom", fontSize: "13px" },
        dataLabels: { enabled: true, formatter: (v) => `${v.toFixed(0)}%` },
        stroke: { width: 2 },
        plotOptions: {
          pie: {
            donut: {
              size: "68%",
              labels: {
                show: true,
                total: {
                  show: true,
                  label: "Total",
                  fontSize: "13px",
                  color: "#64748b",
                  formatter: () => fmtNumero(series.reduce((a, b) => a + b, 0)),
                },
              },
            },
          },
        },
        tooltip: { y: { formatter: (v) => fmtNumero(v) + " llamadas" } },
      },
    };
  }, [stats]);

  /* ---------- Gráfica: volumen por hora (área) ---------- */
  const areaPorHora = useMemo(() => {
    const base = Array.from({ length: 24 }, (_, h) => ({ hour: h, total: 0, answered: 0 }));
    (porHora || []).forEach((r) => {
      const h = Number(r.hour);
      if (h >= 0 && h < 24) {
        base[h].total = Number(r.call_count || r.total || 0);
        base[h].answered = Number(r.answered || 0);
      }
    });
    return {
      series: [
        { name: "Total", data: base.map((b) => b.total) },
        { name: "Contestadas", data: base.map((b) => b.answered) },
      ],
      options: {
        chart: {
          type: "area",
          fontFamily: "inherit",
          toolbar: { show: false },
          zoom: { enabled: false },
        },
        colors: ["#4f46e5", "#10b981"],
        dataLabels: { enabled: false },
        stroke: { curve: "smooth", width: 2.5 },
        fill: {
          type: "gradient",
          gradient: { shadeIntensity: 1, opacityFrom: 0.4, opacityTo: 0.05, stops: [0, 90, 100] },
        },
        xaxis: {
          categories: base.map((b) => `${String(b.hour).padStart(2, "0")}:00`),
          labels: { style: { colors: "#94a3b8", fontSize: "11px" } },
          axisBorder: { show: false },
          axisTicks: { show: false },
        },
        yaxis: { labels: { style: { colors: "#94a3b8" } } },
        grid: { borderColor: "#eef2f7", strokeDashArray: 4 },
        legend: { position: "top", horizontalAlign: "right" },
        tooltip: { shared: true },
      },
    };
  }, [porHora]);

  /* ---------- Gráfica: top extensiones (barras horizontales) ---------- */
  const barrasExtension = useMemo(() => {
    const top = [...(porExtension || [])]
      .sort((a, b) => (Number(b.total_calls) || 0) - (Number(a.total_calls) || 0))
      .slice(0, 10);
    return {
      hayDatos: top.length > 0,
      series: [
        { name: "Entrantes", data: top.map((e) => Number(pick(e, "inbound_calls", "inbound")) || 0) },
        { name: "Salientes", data: top.map((e) => Number(pick(e, "outbound_calls", "outbound")) || 0) },
      ],
      options: {
        chart: { type: "bar", stacked: true, fontFamily: "inherit", toolbar: { show: false } },
        colors: ["#06b6d4", "#4f46e5"],
        plotOptions: { bar: { horizontal: true, borderRadius: 6, barHeight: "62%" } },
        dataLabels: { enabled: false },
        xaxis: {
          categories: top.map((e) => e.extension || "-"),
          labels: { style: { colors: "#94a3b8" } },
        },
        yaxis: { labels: { style: { colors: "#64748b", fontSize: "12px" } } },
        grid: { borderColor: "#eef2f7", strokeDashArray: 4 },
        legend: { position: "top", horizontalAlign: "right" },
        tooltip: { y: { formatter: (v) => fmtNumero(v) + " llamadas" } },
      },
    };
  }, [porExtension]);

  const topExtensiones = useMemo(
    () =>
      [...(porExtension || [])]
        .sort((a, b) => (Number(b.total_calls) || 0) - (Number(a.total_calls) || 0))
        .slice(0, 8),
    [porExtension]
  );

  /* ---------- Render ---------- */
  return (
    <SiteWrapper>
      <div className="dash-wrap">
        {/* Encabezado */}
        <div className="dash-header">
          <div>
            <h1>Dashboard de Llamadas</h1>
            <div className="dash-subtitle">
              Métricas de telefonía y centro de contacto en tiempo real
            </div>
          </div>
          <button className="dash-btn" onClick={cargarTodo} disabled={cargando}>
            <RefreshCw size={16} className={cargando ? "spin" : ""} />
            {cargando ? "Actualizando..." : "Actualizar"}
          </button>
        </div>

        {/* Filtros */}
        <div className="dash-filters">
          <div className="dash-field">
            <label>Desde</label>
            <input
              type="date"
              value={fechaInicio}
              max={fechaFin}
              onChange={(e) => setFechaInicio(e.target.value)}
            />
          </div>
          <div className="dash-field">
            <label>Hasta</label>
            <input
              type="date"
              value={fechaFin}
              min={fechaInicio}
              max={hoyISO()}
              onChange={(e) => setFechaFin(e.target.value)}
            />
          </div>
          <button className="dash-btn" onClick={cargarTodo} disabled={cargando}>
            Aplicar
          </button>
          <button
            className="dash-btn ghost"
            onClick={() => {
              setFechaInicio(haceDiasISO(7));
              setFechaFin(hoyISO());
            }}
            disabled={cargando}
          >
            Últimos 7 días
          </button>
          <button
            className="dash-btn ghost"
            onClick={() => {
              setFechaInicio(haceDiasISO(30));
              setFechaFin(hoyISO());
            }}
            disabled={cargando}
          >
            Últimos 30 días
          </button>
        </div>

        {/* KPIs */}
        <div className="dash-kpis">
          {kpis.map((k, i) => (
            <div className="kpi-card" key={i} style={{ "--kpi-accent": k.accent }}>
              <div className="kpi-icon" style={{ background: k.accent }}>
                {k.icon}
              </div>
              <div className="kpi-label">{k.label}</div>
              <div className="kpi-value">{k.value}</div>
              <div className="kpi-sub">
                {k.live ? <span className="live-dot" /> : null}
                {k.sub}
              </div>
            </div>
          ))}
        </div>

        {cargando ? (
          <div className="panel">
            <div className="dash-spinner" />
            <div className="dash-empty">Cargando información del dashboard…</div>
          </div>
        ) : (
          <>
            {/* Gráficas principales */}
            <div className="dash-grid" style={{ marginBottom: 18 }}>
              <div className="panel col-8">
                <div className="panel-head">
                  <div>
                    <h3 className="panel-title">Volumen de llamadas por hora</h3>
                    <div className="panel-desc">Distribución horaria del periodo seleccionado</div>
                  </div>
                </div>
                <ReactApexChart
                  options={areaPorHora.options}
                  series={areaPorHora.series}
                  type="area"
                  height={320}
                />
              </div>

              <div className="panel col-4">
                <div className="panel-head">
                  <div>
                    <h3 className="panel-title">Resultado de llamadas</h3>
                    <div className="panel-desc">Por disposición</div>
                  </div>
                </div>
                <ReactApexChart
                  options={donaDisposition.options}
                  series={donaDisposition.series}
                  type="donut"
                  height={320}
                />
              </div>
            </div>

            {/* Extensiones + grabaciones */}
            <div className="dash-grid">
              <div className="panel col-8">
                <div className="panel-head">
                  <div>
                    <h3 className="panel-title">Top extensiones por volumen</h3>
                    <div className="panel-desc">Entrantes vs. salientes (10 primeras)</div>
                  </div>
                </div>
                {barrasExtension.hayDatos ? (
                  <ReactApexChart
                    options={barrasExtension.options}
                    series={barrasExtension.series}
                    type="bar"
                    height={360}
                  />
                ) : (
                  <div className="dash-empty">Sin datos de extensiones en el periodo.</div>
                )}
              </div>

              <div className="panel col-4">
                <div className="panel-head">
                  <div>
                    <h3 className="panel-title">
                      <Mic size={16} style={{ verticalAlign: "-2px", marginRight: 6 }} />
                      Grabaciones
                    </h3>
                    <div className="panel-desc">Resumen del periodo</div>
                  </div>
                </div>
                {recStats ? (
                  <div>
                    <div className="rec-row">
                      <span>Total grabaciones</span>
                      <strong>{fmtNumero(recStats.total_recordings)}</strong>
                    </div>
                    <div className="rec-row">
                      <span>Duración total</span>
                      <strong>{fmtDuracion(recStats.total_duration_seconds)}</strong>
                    </div>
                    <div className="rec-row">
                      <span>Duración promedio</span>
                      <strong>{fmtDuracion(recStats.avg_duration_seconds)}</strong>
                    </div>
                    <div className="rec-row">
                      <span>Almacenamiento</span>
                      <strong>
                        {fmtGB(recStats.total_size_bytes)}
                      </strong>
                    </div>
                    <div className="rec-row">
                      <span>Transcritas</span>
                      <strong>{fmtNumero(recStats.transcribed_count)}</strong>
                    </div>
                    <div className="rec-row">
                      <span>Transcripción pendiente</span>
                      <span className="dash-chip amber">
                        {fmtNumero(recStats.transcription_pending_count)}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="dash-empty">Sin datos de grabaciones.</div>
                )}
              </div>
            </div>

            {/* Tabla de extensiones + llamadas en vivo */}
            <div className="dash-grid" style={{ marginTop: 18 }}>
              <div className="panel col-6">
                <div className="panel-head">
                  <div>
                    <h3 className="panel-title">
                      <Users size={16} style={{ verticalAlign: "-2px", marginRight: 6 }} />
                      Detalle por extensión
                    </h3>
                    <div className="panel-desc">Rendimiento individual</div>
                  </div>
                </div>
                {topExtensiones.length ? (
                  <table className="dash-table">
                    <thead>
                      <tr>
                        <th>Ext.</th>
                        <th>Nombre</th>
                        <th>Total</th>
                        <th>
                          <PhoneIncoming size={13} style={{ verticalAlign: "-1px" }} /> In
                        </th>
                        <th>
                          <PhoneOutgoing size={13} style={{ verticalAlign: "-1px" }} /> Out
                        </th>
                        <th>Contest.</th>
                        <th>Prom.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topExtensiones.map((e, i) => (
                        <tr key={i}>
                          <td>
                            <strong>{e.extension || "-"}</strong>
                          </td>
                          <td className="text-truncate" title={e.display_name}>
                            {e.display_name || <span style={{ opacity: 0.5 }}>-</span>}
                          </td>
                          <td>{fmtNumero(e.total_calls)}</td>
                          <td>{fmtNumero(pick(e, "inbound_calls", "inbound"))}</td>
                          <td>{fmtNumero(pick(e, "outbound_calls", "outbound"))}</td>
                          <td>
                            <span className="dash-chip green">
                              {fmtNumero(pick(e, "answered_calls", "answered"))}
                            </span>
                          </td>
                          <td>{fmtDuracion(e.avg_duration)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="dash-empty">Sin datos de extensiones.</div>
                )}
              </div>

              <div className="panel col-6">
                <div className="panel-head">
                  <div>
                    <h3 className="panel-title">
                      <span className="live-dot" />
                      Llamadas en vivo
                    </h3>
                    <div className="panel-desc">Actualización automática cada 10s</div>
                  </div>
                  <span className="dash-chip blue">{fmtNumero(liveCalls.length)} activas</span>
                </div>
                {liveCalls.length ? (
                  <table className="dash-table">
                    <thead>
                      <tr>
                        <th>Origen</th>
                        <th>Destino</th>
                        <th>Dirección</th>
                        <th>Estado</th>
                        <th>Duración</th>
                      </tr>
                    </thead>
                    <tbody>
                      {liveCalls.map((c, i) => (
                        <tr key={i}>
                          <td>{c.source || "-"}</td>
                          <td>{c.destination || "-"}</td>
                          <td>
                            {c.direction === "inbound" ? (
                              <span className="dash-chip blue">Entrante</span>
                            ) : c.direction === "outbound" ? (
                              <span className="dash-chip amber">Saliente</span>
                            ) : (
                              <span className="dash-chip">{c.direction || "-"}</span>
                            )}
                          </td>
                          <td>
                            {c.status === "answered" ? (
                              <span className="dash-chip green">En curso</span>
                            ) : c.status === "ringing" ? (
                              <span className="dash-chip amber">Timbrando</span>
                            ) : (
                              <span className="dash-chip">{c.status || "-"}</span>
                            )}
                          </td>
                          <td>{fmtDuracion(c.duration_seconds)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="dash-empty">
                    <PhoneCall size={28} style={{ opacity: 0.3, marginBottom: 8 }} />
                    <div>No hay llamadas activas en este momento.</div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </SiteWrapper>
  );
};

export default DashboardLlamadasPage;
